/* respell-core.js — the pure half of /pronunciation-respelling: everything
 * that decides WHAT is on the page, with no DOM, so it is unit-tested in node
 * (tests/js/respell-core.test.mjs). respell-app.js draws it.
 *
 *   RespellCore.splitLines(text)        → [[{t, kind}]] kind: en|native|num|punct|space
 *   RespellCore.fixHomoglyphs(text)     → {text, fixed:[[from,to]]}
 *   RespellCore.nativeScriptOf(word)    → 'Cyrl'|'Hang'|'Arab'|'Latn-ext'|null
 *   RespellCore.plainText(lines, pick)  → the respelling as flowing text
 *   RespellCore.tsv(rows)               → a table the clipboard pastes into a sheet
 *   RespellCore.encodeShare / decodeShare
 *   RespellCore.preferVariant(cands, v) → British or American word first
 *   RespellCore.spoken(line, pick)      → {text, spans} what a voice reads
 *   RespellCore.spanAt(spans, charIndex) → the word a boundary belongs to
 *   RespellCore.chunkLines(texts, first, max) → requests for a server voice
 *
 * Plain IIFE, no dependencies. Exposes window.RespellCore + module.exports.
 */
(function (root, factory) {
  'use strict';
  var C = factory();
  if (typeof module !== 'undefined' && module.exports) module.exports = C;
  if (typeof window !== 'undefined') window.RespellCore = C;
  else if (root) root.RespellCore = C;
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  /* Words, numbers, punctuation and spaces of one line, in order. An English
   * word keeps its apostrophe (don't, it's); a word in another script is kept
   * whole so the page can look it up in the reader's language. */
  var TOKEN = /[A-Za-zÀ-ɏ]+(?:['’][A-Za-zÀ-ɏ]+)*|[Ͱ-ϿЀ-ԯ؀-ۿݐ-ݿᄀ-ᇿ㄰-㆏가-힣]+(?:['’-][Ѐ-ԯ가-힣]+)*|\d+(?:[.,]\d+)*|\s+|[^\sA-Za-z\d]/g;
  function kindOf(t) {
    if (/^\s+$/.test(t)) return 'space';
    if (/^[A-Za-z]/.test(t)) return /[^\x00-\x7F]/.test(t) ? 'native' : 'en';
    if (/^\d/.test(t)) return 'num';
    if (/[À-ɏͰ-ϿЀ-ԯ؀-ۿݐ-ݿᄀ-ᇿ㄰-㆏가-힣]/.test(t)) return 'native';
    return 'punct';
  }
  function splitLines(text) {
    var lines = String(text || '').replace(/\r\n?/g, '\n').split('\n');
    return lines.map(function (line) {
      var m = line.match(TOKEN) || [];
      return m.map(function (t) { return { t: t, kind: kindOf(t) }; });
    });
  }

  /* Cyrillic letters that look Latin, typed inside an English word from a
   * Russian keyboard: «сat» (Cyrillic с), «hоme» (Cyrillic о). Such a word is
   * in no dictionary, so it would silently fall to the reading-rules guess.
   * Only a word that is MOSTLY Latin is repaired — a Russian word stays Russian. */
  // lower case: only the letters that are drawn the same (к, м, т, в, н are not)
  var LOOKALIKE = { 'а': 'a', 'е': 'e', 'о': 'o', 'р': 'p', 'с': 'c', 'у': 'y', 'х': 'x',
    'і': 'i', 'ј': 'j',
    'А': 'A', 'Е': 'E', 'О': 'O', 'Р': 'P', 'С': 'C', 'У': 'Y', 'Х': 'X', 'К': 'K',
    'М': 'M', 'Т': 'T', 'В': 'B', 'Н': 'H', 'І': 'I' };
  function fixHomoglyphs(text) {
    var fixed = [];
    var NL = String.fromCharCode(10);
    var out = String(text || '').split(NL).map(function (line) {
      // A whole-Cyrillic word made only of lookalikes (Му, Не) is repaired only
      // inside a line that is plainly English — on its own it may be Russian.
      var ws = line.match(/[A-Za-zЀ-ӿ]+/g) || [], latWords = 0;
      for (var q = 0; q < ws.length; q++) if (/[A-Za-z]/.test(ws[q])) latWords++;
      var englishLine = ws.length >= 3 && latWords / ws.length >= 0.6;
      return line.replace(/[A-Za-zЀ-ӿ]+/g, function (w) {
        if (!/[Ѐ-ӿ]/.test(w)) return w;
        var lat = 0, cyr = 0, convertible = true;
        for (var i = 0; i < w.length; i++) {
          var ch = w.charAt(i);
          if (/[A-Za-z]/.test(ch)) lat++;
          else { cyr++; if (!LOOKALIKE.hasOwnProperty(ch)) convertible = false; }
        }
        if (!convertible) return w;
        if (lat === 0 ? !englishLine : lat < cyr) return w;
        var fw = w.replace(/[Ѐ-ӿ]/g, function (c) { return LOOKALIKE[c]; });
        fixed.push([w, fw]);
        return fw;
      });
    }).join(NL);
    return { text: out, fixed: fixed };
  }

  function nativeScriptOf(w) {
    if (/[Ѐ-ԯ]/.test(w)) return 'Cyrl';
    if (/[ᄀ-ᇿ㄰-㆏가-힣]/.test(w)) return 'Hang';
    if (/[؀-ۿݐ-ݿ]/.test(w)) return 'Arab';
    if (/[À-ɏ]/.test(w)) return 'Latn-ext';
    return null;
  }

  /* The respelling as a text a person reads aloud: every English word replaced
   * by pick(token, lineIndex, tokenIndex) (the page passes its respelling), all
   * punctuation, spacing and line breaks kept. */
  function plainText(lines, pick) {
    return lines.map(function (line, li) {
      return line.map(function (tok, ti) {
        if (tok.kind === 'en' || tok.kind === 'native') {
          var r = pick(tok, li, ti);
          return r == null ? tok.t : r;
        }
        return tok.t;
      }).join('');
    }).join('\n');
  }

  /* Tab-separated rows; a cell never carries a tab or a line break. */
  function tsv(rows) {
    return rows.map(function (r) {
      return r.map(function (c) { return String(c == null ? '' : c).replace(/[\t\r\n]+/g, ' '); }).join('\t');
    }).join('\n');
  }

  /* Share link: the text rides in the URL fragment, which a browser never
   * sends to the server. base64url of UTF-8. */
  function b64urlEncode(str) {
    var bytes = utf8(str), bin = '';
    for (var i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    var b = (typeof btoa === 'function') ? btoa(bin) : Buffer.from(bin, 'binary').toString('base64');
    return b.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
  function b64urlDecode(s) {
    s = String(s || '').replace(/-/g, '+').replace(/_/g, '/');
    while (s.length % 4) s += '=';
    var bin = (typeof atob === 'function') ? atob(s) : Buffer.from(s, 'base64').toString('binary');
    var bytes = new Uint8Array(bin.length);
    for (var i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return fromUtf8(bytes);
  }
  function utf8(str) {
    if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(str);
    return Buffer.from(str, 'utf8');
  }
  function fromUtf8(bytes) {
    if (typeof TextDecoder !== 'undefined') return new TextDecoder().decode(bytes);
    return Buffer.from(bytes).toString('utf8');
  }
  var SHARE_KEYS = { t: 'text', s: 'script', a: 'accent', v: 'view' };
  function encodeShare(o) {
    var parts = [];
    if (o.text) parts.push('t=' + b64urlEncode(o.text));
    if (o.script) parts.push('s=' + encodeURIComponent(o.script));
    if (o.accent) parts.push('a=' + encodeURIComponent(o.accent));
    if (o.view) parts.push('v=' + encodeURIComponent(o.view));
    return '#' + parts.join('&');
  }
  function decodeShare(hash) {
    var h = String(hash || '').replace(/^#/, '');
    if (!h) return null;
    var out = {};
    h.split('&').forEach(function (kv) {
      var i = kv.indexOf('='); if (i < 0) return;
      var k = SHARE_KEYS[kv.slice(0, i)], v = kv.slice(i + 1);
      if (!k) return;
      try { out[k] = k === 'text' ? b64urlDecode(v) : decodeURIComponent(v); } catch (e) { /* a torn link loses one field, not the page */ }
    });
    return out.text ? out : null;
  }

  /* British and American words for the same thing. When the reader chose one
   * variant, its word leads the candidates a reverse lookup found (мама →
   * mum for UK, mom for US) — only when BOTH are among them. */
  var UK_US = [['mum', 'mom'], ['mummy', 'mommy'], ['flat', 'apartment'], ['lift', 'elevator'],
    ['lorry', 'truck'], ['biscuit', 'cookie'], ['sweets', 'candy'], ['trousers', 'pants'],
    ['autumn', 'fall'], ['football', 'soccer'], ['petrol', 'gas'], ['holiday', 'vacation'],
    ['pavement', 'sidewalk'], ['maths', 'math'], ['rubbish', 'garbage'], ['queue', 'line'],
    ['film', 'movie'], ['shop', 'store'], ['timetable', 'schedule'], ['motorway', 'highway'],
    ['tap', 'faucet'], ['nappy', 'diaper'], ['torch', 'flashlight'], ['post', 'mail'],
    ['chemist', 'drugstore'], ['crisps', 'chips'], ['wardrobe', 'closet'], ['underground', 'subway']];
  function preferVariant(cands, variant) {
    if (!cands || cands.length < 2) return cands ? cands.slice() : [];
    var out = cands.slice();
    for (var i = 0; i < UK_US.length; i++) {
      var want = variant === 'us' ? UK_US[i][1] : UK_US[i][0];
      var other = variant === 'us' ? UK_US[i][0] : UK_US[i][1];
      var wi = out.indexOf(want), oi = out.indexOf(other);
      if (wi > 0 && oi >= 0 && oi < wi) { out.splice(wi, 1); out.splice(oi, 0, want); }
    }
    return out;
  }

  /* What a voice reads for one line, and where each word sits in it, so an
   * engine's word boundary (a character index) finds its word again:
   *   spoken(line, pick) → {text, spans:[{start, end, ti}]}
   * pick(tok, ti) → the string to read for a word token (English as typed, a
   * word typed in the reader's language → the English word chosen for it,
   * null → not read). Spaces collapse to one; punctuation is kept, because
   * it is what makes a voice pause. */
  function spoken(line, pick) {
    var text = '', spans = [], pendingSpace = false;
    for (var ti = 0; ti < line.length; ti++) {
      var tok = line[ti];
      if (tok.kind === 'space') { if (text) pendingSpace = true; continue; }
      var s = (tok.kind === 'punct') ? tok.t : pick(tok, ti);
      if (s == null || s === '') continue;
      if (pendingSpace) { text += ' '; pendingSpace = false; }
      if (tok.kind !== 'punct') spans.push({ start: text.length, end: text.length + s.length, ti: ti });
      text += s;
    }
    return { text: text, spans: spans };
  }
  /* The word a boundary at `charIndex` belongs to (-1 when none). Some engines
   * report the space before a word, so a boundary between words picks the
   * next one. */
  function spanAt(spans, charIndex) {
    for (var i = 0; i < spans.length; i++) {
      if (charIndex < spans[i].end) return charIndex >= spans[i].start || i === 0 || charIndex >= spans[i - 1].end ? spans[i].ti : spans[i - 1].ti;
    }
    return spans.length ? spans[spans.length - 1].ti : -1;
  }
  /* Lines grouped into requests for a server voice: the FIRST group small,
   * because it is the one the person waits for, the rest up to `max`
   * characters. A line longer than the ceiling travels alone. → [[index…]] */
  function chunkLines(texts, first, max) {
    var out = [], cur = [], len = 0;
    for (var i = 0; i < texts.length; i++) {
      var t = String(texts[i] || '');
      if (!t) continue;
      var ceiling = out.length ? max : first;
      if (cur.length && len + 1 + t.length > ceiling) { out.push(cur); cur = []; len = 0; }
      cur.push(i); len += (len ? 1 : 0) + t.length;
    }
    if (cur.length) out.push(cur);
    return out;
  }

  return {
    splitLines: splitLines,
    kindOf: kindOf,
    fixHomoglyphs: fixHomoglyphs,
    nativeScriptOf: nativeScriptOf,
    plainText: plainText,
    tsv: tsv,
    encodeShare: encodeShare,
    decodeShare: decodeShare,
    preferVariant: preferVariant,
    spoken: spoken,
    spanAt: spanAt,
    chunkLines: chunkLines
  };
});
